/**
 * Live cashier adapter over the HRW cashier BFF (`/api/v1/cashier/*`).
 *
 * The backend is the authority for every verdict (capability, KYC, limits,
 * balance); this adapter only shapes responses for the panes. Amounts cross
 * the wire in integer cents; provider payloads (Apple Pay tokens) relay
 * verbatim and are never stored or logged. Donor-shape normalization
 * (Coinflow multi-speed quotes, linked-instrument sweeps) mirrors
 * monkeytilt-core's cashier API layer.
 */

import { ApiError, apiGet, apiPost } from "@/api/client";

import type {
  CashierActionRequest,
  CashierActionResult,
  CashierAdapter,
  CashierApplePayReadiness,
  CashierApplePaySessionHandlers,
  CashierCryptoAddress,
  CashierCryptoAsset,
  CashierDepositLimits,
  CashierGooglePayContext,
  CashierMethodView,
  CashierQuote,
  CashierQuoteRequest,
  CashierSnapshot,
  CashierTransactionView,
  CashierWithdrawAccount,
  CashierWithdrawContext,
} from "./types";
import { CASHIER_ADAPTER_VERSION } from "./types";

// ---------------------------------------------------------------------------
// Minimal ApplePaySession ambient surface (lib.dom omits it).
// ---------------------------------------------------------------------------

type ApplePayPaymentAuthorizedEvent = { payment: unknown };

type ApplePaySessionInstance = {
  begin: () => void;
  abort: () => void;
  completeMerchantValidation: (session: unknown) => void;
  completePayment: (status: number) => void;
  onvalidatemerchant: (() => void) | null;
  onpaymentauthorized: ((event: ApplePayPaymentAuthorizedEvent) => void) | null;
  oncancel: (() => void) | null;
};

type ApplePaySessionCtor = {
  new (version: number, request: unknown): ApplePaySessionInstance;
  canMakePayments: () => boolean;
  STATUS_SUCCESS: number;
  STATUS_FAILURE: number;
};

declare global {
  interface Window {
    ApplePaySession?: ApplePaySessionCtor;
  }
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

export function centsFromAmount(amount: string): number {
  const parsed = Number.parseFloat(amount);
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return Math.round(parsed * 100);
}

function usd(cents: number): string {
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
}

/**
 * Stable per-browser device reference forwarded as `X-Device-ID`. Not an
 * nSure fingerprint — a continuity signal for the payments fraud row until
 * the nSure SDK config ships through the BFF.
 */
function deviceId(): string {
  const key = "hw:cashier:device-id";
  try {
    const existing = window.localStorage.getItem(key);
    if (existing) return existing;
    const minted = crypto.randomUUID();
    window.localStorage.setItem(key, minted);
    return minted;
  } catch {
    return "";
  }
}

function withdrawReturnUrl(): string {
  return `${window.location.origin}/api/v1/cashier/withdraw/return`;
}

const record = (value: unknown): Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const str = (value: unknown): string =>
  typeof value === "string" ? value : "";

const num = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

function centsFromMoneyString(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.round(value * 100);
  }
  const text = str(value);
  if (!text) return null;
  const parsed = Number.parseFloat(text);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : null;
}

// ---------------------------------------------------------------------------
// Withdraw methods normalization (donor sweep, scoped to shipped rails)
// ---------------------------------------------------------------------------

const SPEED_LABELS: Record<string, string> = {
  card: "Push to Card",
  asap: "RTP Bank Transfer",
  same_day: "Same-Day ACH",
  standard: "Standard ACH (2-3 days)",
  venmo: "Venmo",
  paypal: "PayPal",
};

export function speedLabel(speed: string): string {
  return SPEED_LABELS[speed] ?? speed.replace(/_/g, " ");
}

function normalizeWithdrawContext(payload: unknown): CashierWithdrawContext {
  const data = record(payload);
  const accounts: CashierWithdrawAccount[] = [];

  const cards = Array.isArray(data.cards) ? data.cards : [];
  for (const raw of cards) {
    const card = record(raw);
    if (!str(card.token) || card.isDeleted === true) continue;
    accounts.push({
      token: str(card.token),
      kind: "card",
      label: `${str(card.type) || "Card"} ****${str(card.last4)}`.trim(),
      detail: null,
      speeds: ["card", "asap"],
    });
  }

  const banks = Array.isArray(data.bankAccounts)
    ? data.bankAccounts
    : Array.isArray(data.bank_accounts)
      ? data.bank_accounts
      : [];
  for (const raw of banks) {
    const bank = record(raw);
    if (!str(bank.token) || bank.isDeleted === true) continue;
    const alias = str(bank.alias) || str(bank.name) || "Bank";
    accounts.push({
      token: str(bank.token),
      kind: "bank",
      label: `${alias} ****${str(bank.last4)}`.trim(),
      detail: str(bank.type) || null,
      speeds: ["same_day", "standard"],
    });
  }

  const limits = record(data.withdrawal_limits);
  return {
    needsOnboarding:
      data.needs_onboarding === true || data.needsOnboarding === true,
    bankAuthUrl: str(data.bank_auth_url) || str(data.bankAuthUrl) || null,
    accounts,
    minWithdrawalCents: num(limits.minWithdrawalCents),
    maxWithdrawalCents: num(limits.maxWithdrawalCents),
  };
}

function normalizeDepositLimits(payload: unknown): CashierDepositLimits {
  const data = record(payload);
  const methods = Array.isArray(data.methods) ? data.methods : [];
  return {
    methods: methods.map((raw) => {
      const row = record(raw);
      return {
        id: str(row.id) || str(row.method),
        name: str(row.name) || str(row.displayName) || str(row.display_name),
        min: str(row.min) || null,
        max: str(row.max) || null,
      };
    }),
    perPurchaseMax: str(data.perPurchaseMax) || null,
  };
}

// ---------------------------------------------------------------------------
// Withdraw quote normalization (donor-verbatim fee semantics)
// ---------------------------------------------------------------------------

export class QuoteUnavailableError extends Error {
  readonly reason: string;

  constructor(reason: string) {
    super(reason || "No delivery speed is available for this amount.");
    this.name = "QuoteUnavailableError";
    this.reason = reason;
  }
}

function quotesFromCoinflowResponse(
  payload: Record<string, unknown>,
  requestedCents: number,
  speeds: readonly string[],
): CashierQuote[] {
  const availability = record(payload.speed_availability);
  const availableSpeeds = Array.isArray(payload.available_speeds)
    ? (payload.available_speeds as unknown[]).map(String)
    : null;
  const usable = availableSpeeds
    ? speeds.filter((speed) => availableSpeeds.includes(speed))
    : [...speeds];

  const quotes: CashierQuote[] = [];
  for (const speed of usable) {
    const block = record(payload[speed]);
    if (!("fee" in block) && !("finalSettlement" in block)) continue;
    const av = record(availability[speed]);
    // TOTAL fee: payments' speed_availability sums Coinflow's fee and the
    // merchant surcharge; the raw fee block alone under-reports it.
    const feeCents =
      num(av.fee_cents) ??
      (num(record(block.fee).cents) ?? 0) +
        (num(record(block.customFee).cents) ?? 0);
    const netCents = num(record(block.finalSettlement).cents) ?? 0;
    quotes.push({
      id: speed,
      amount: usd(requestedCents),
      fee: usd(feeCents),
      total: usd(netCents),
      expiresAt: null,
      disclosure: str(block.expectedDeliveryDate) || null,
      feeCents,
      netCents,
    });
  }

  // Collapse speeds with identical fee/payout/ETA — a distinction without
  // a difference. First wins (speed order puts the preferred rail first).
  const seen = new Set<string>();
  const deduped = quotes.filter((quote) => {
    const key = `${quote.feeCents}|${quote.netCents}|${quote.disclosure}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  if (deduped.length === 0) {
    const entry =
      record(availability[speeds[0] ?? ""]) ??
      record(Object.values(availability)[0]);
    throw new QuoteUnavailableError(str(entry.reason));
  }
  return deduped;
}

function quoteFromStubResponse(
  payload: Record<string, unknown>,
  requestedCents: number,
): CashierQuote[] {
  const feeCents = centsFromMoneyString(payload.fee) ?? 0;
  const netCents =
    centsFromMoneyString(payload.net_amount) ?? requestedCents - feeCents;
  const speed = str(payload.speed) || "standard";
  return [
    {
      id: speed,
      amount: usd(requestedCents),
      fee: usd(feeCents),
      total: usd(netCents),
      expiresAt: null,
      disclosure: null,
      feeCents,
      netCents,
    },
  ];
}

// ---------------------------------------------------------------------------
// Apple Pay config probe + session driver
// ---------------------------------------------------------------------------

type ApplePayConfig = {
  enabled: boolean;
  merchantName: string;
};

let applePayConfigCache: ApplePayConfig | null | undefined;

async function fetchApplePayConfig(): Promise<ApplePayConfig | null> {
  if (applePayConfigCache !== undefined) return applePayConfigCache;
  try {
    const data = await apiGet<{ enabled: boolean; merchantName?: string }>(
      "/cashier/apple-pay/config",
    );
    applePayConfigCache = {
      enabled: data.enabled === true,
      merchantName: data.merchantName || "MonkeyTilt",
    };
  } catch {
    // Missing rail (404) or an outage — the method hides either way.
    applePayConfigCache = null;
  }
  return applePayConfigCache;
}

function applePaySupported(): boolean {
  try {
    return (
      typeof window.ApplePaySession !== "undefined" &&
      window.ApplePaySession.canMakePayments()
    );
  } catch {
    return false;
  }
}

/** Player-facing mapping of the payments Apple Pay error envelope. */
function applePayErrorMessage(error: unknown): string {
  // The backend envelope uppercases `error.code` and carries the
  // donor-exact lowercase code in `details.errorCode` — match on that.
  const code =
    error instanceof ApiError
      ? str(record(error.details).errorCode) ||
        str(record(error.details).code) ||
        error.code.toLowerCase()
      : "";
  switch (code) {
    case "apple_pay_unavailable":
    case "merchant_validation_failed":
      return "Apple Pay is unavailable right now — you can pay by card instead.";
    case "card_declined":
      return "Payment declined. You can try again or use a different card.";
    case "customer_blocked":
      return "This payment could not be processed. Please contact support.";
    case "verification_required":
      return "Identity verification is required — complete it, then try again.";
    case "limit_exceeded":
      return "That amount is over your current deposit limit. Try a smaller amount.";
    default:
      return "Something went wrong. Please try again.";
  }
}

function buildApplePayAdapter() {
  let activeSession: ApplePaySessionInstance | null = null;

  const getReadiness = async (): Promise<CashierApplePayReadiness> => {
    if (!applePaySupported()) {
      return {
        state: "unsupported",
        message: "Apple Pay needs Safari on an Apple device.",
      };
    }
    const config = await fetchApplePayConfig();
    if (!config?.enabled) {
      return {
        state: "unavailable",
        message: "Apple Pay is not enabled for this cashier yet.",
      };
    }
    return { state: "ready", message: null };
  };

  const beginSession = async (
    request: CashierQuoteRequest,
    handlers: CashierApplePaySessionHandlers,
  ): Promise<void> => {
    const ApplePaySession = window.ApplePaySession;
    const amountCents = centsFromAmount(request.amount);
    if (!ApplePaySession || amountCents <= 0) {
      handlers.onError();
      return;
    }
    const config = await fetchApplePayConfig();
    if (!config?.enabled) {
      handlers.onError();
      return;
    }

    // The sheet total MUST be the payments-computed subtotal + customer fee
    // — the token authorizes exactly this figure.
    let totals: { subtotalCents: number; feeCents: number; totalCents: number };
    try {
      totals = await apiPost<{
        subtotalCents: number;
        feeCents: number;
        totalCents: number;
      }>("/cashier/apple-pay/totals", { amountCents });
    } catch {
      handlers.onError();
      return;
    }

    const session = new ApplePaySession(3, {
      countryCode: "US",
      currencyCode: "USD",
      supportedNetworks: ["visa", "masterCard", "amex", "discover"],
      merchantCapabilities: ["supports3DS"],
      lineItems: [
        { label: "Deposit", amount: (totals.subtotalCents / 100).toFixed(2) },
        {
          label: "Processing fee",
          amount: (totals.feeCents / 100).toFixed(2),
        },
      ],
      // Coinflow requires billing name + postal address (chargeback
      // scoring) and the customer email (shipping contact is the only
      // slot Apple returns it on).
      requiredBillingContactFields: ["postalAddress", "name"],
      requiredShippingContactFields: ["email"],
      total: {
        label: `${config.merchantName} Deposit`,
        amount: (totals.totalCents / 100).toFixed(2),
      },
    });
    activeSession = session;

    session.onvalidatemerchant = () => {
      apiPost<Record<string, unknown>>(
        "/cashier/apple-pay/merchant-session",
        {},
      )
        .then((merchantSession) => {
          session.completeMerchantValidation(merchantSession);
        })
        .catch((error: unknown) => {
          session.abort();
          activeSession = null;
          handlers.onStatus(applePayErrorMessage(error));
          handlers.onError();
        });
    };

    session.onpaymentauthorized = (event) => {
      // event.payment is single-use and expires in seconds — relay
      // immediately, never store or log it.
      apiPost<{ status: string; transactionId?: string }>(
        "/cashier/apple-pay/charge",
        {
          amountCents,
          applePayPayment: event.payment,
          deviceId: deviceId(),
        },
      )
        .then(() => {
          session.completePayment(ApplePaySession.STATUS_SUCCESS);
          activeSession = null;
          handlers.onComplete({
            state: "pending",
            message: "Processing your payment…",
          });
        })
        .catch((error: unknown) => {
          // Dismiss the sheet FIRST or it hangs.
          session.completePayment(ApplePaySession.STATUS_FAILURE);
          activeSession = null;
          handlers.onStatus(applePayErrorMessage(error));
          handlers.onError();
        });
    };

    session.oncancel = () => {
      activeSession = null;
      handlers.onCancel();
    };
    session.begin();
  };

  const cancelSession = () => {
    try {
      activeSession?.abort();
    } catch {
      // Session already dismissed.
    }
    activeSession = null;
  };

  return { getReadiness, beginSession, cancelSession };
}

// ---------------------------------------------------------------------------
// Crypto + Google Pay context (fail-soft: an outage hides the rail)
// ---------------------------------------------------------------------------

function normalizeCryptoAssets(payload: unknown): CashierCryptoAsset[] {
  const data = record(payload);
  const assets = Array.isArray(data.assets) ? data.assets : [];
  return assets.map((raw) => {
    const row = record(raw);
    return {
      symbol: str(row.symbol),
      network: str(row.network),
      networkName: str(row.networkName),
      family: str(row.family),
      decimals: num(row.decimals),
      minDepositAmount: str(row.minDepositAmount) || null,
      minWithdrawAmount: str(row.minWithdrawAmount) || null,
      withdrawalFee: str(row.withdrawalFee) || null,
      depositEnabled: row.depositEnabled === true,
      withdrawEnabled: row.withdrawEnabled === true,
      minConfirmations: num(row.minConfirmations),
      imageUrl: str(row.imageUrl) || null,
    };
  });
}

function normalizeGooglePay(payload: unknown): CashierGooglePayContext | null {
  const data = record(payload);
  if (data.enabled !== true || !str(data.gatewayMerchantId)) return null;
  return {
    enabled: true,
    gatewayMerchantId: str(data.gatewayMerchantId),
    merchantId: str(data.merchantId),
    merchantName: str(data.merchantName) || "MonkeyTilt",
    environment: str(data.environment) || "TEST",
  };
}

// ---------------------------------------------------------------------------
// Snapshot assembly
// ---------------------------------------------------------------------------

function depositMethodViews(
  applePay: CashierApplePayReadiness,
  crypto: { assets: readonly CashierCryptoAsset[] } | null,
  googlePay: CashierGooglePayContext | null,
  limits: CashierDepositLimits | null,
): CashierMethodView[] {
  const aliases: Record<string, string> = {
    applepay: "apple_pay",
    google_pay: "googlepay",
    cash_app: "cashapp",
    gateway_io: "gateway",
  };
  const definitions: readonly Omit<CashierMethodView, "detail">[] = [
    {
      id: "apple_pay",
      kind: "apple-pay",
      label: "Apple Pay",
      availability:
        applePay.state === "ready"
          ? "ready"
          : applePay.state === "checking"
            ? "checking"
            : "unavailable",
      currencies: ["USD"],
      unavailableReason: applePay.message,
    },
    {
      id: "googlepay",
      kind: "google-pay",
      label: "Google Pay",
      availability: googlePay?.enabled ? "ready" : "unavailable",
      currencies: ["USD"],
      unavailableReason: googlePay?.enabled ? null : "Unavailable",
    },
    {
      id: "card",
      kind: "card",
      label: "Card / Bank",
      availability: "ready",
      currencies: ["USD"],
      unavailableReason: null,
    },
    {
      id: "cashapp",
      kind: "cash-app",
      label: "Cash App",
      availability: "ready",
      currencies: ["USD"],
      unavailableReason: null,
    },
    {
      id: "paypal",
      kind: "paypal",
      label: "PayPal",
      availability: "ready",
      currencies: ["USD"],
      unavailableReason: null,
    },
    {
      id: "venmo",
      kind: "venmo",
      label: "Venmo",
      availability: "ready",
      currencies: ["USD"],
      unavailableReason: null,
    },
    {
      id: "wire",
      kind: "wire",
      label: "Wire",
      availability: "ready",
      currencies: ["USD"],
      unavailableReason: null,
    },
    {
      id: "interac",
      kind: "interac",
      label: "Interac e-Transfer",
      availability: "ready",
      currencies: ["USD"],
      unavailableReason: null,
    },
    {
      id: "gateway",
      kind: "gateway",
      label: "Gateway IO",
      availability: "ready",
      currencies: ["USD"],
      unavailableReason: null,
    },
  ];
  const advertised = new Map(
    (limits?.methods ?? [])
      .filter((method) => method.id)
      .map((method) => [
        aliases[method.id.toLowerCase()] ?? method.id.toLowerCase(),
        method,
      ]),
  );
  if (googlePay?.enabled && !advertised.has("googlepay")) {
    advertised.set("googlepay", {
      id: "googlepay",
      name: "Google Pay",
      min: null,
      max: null,
    });
  }

  const methods = definitions
    .filter((definition) => advertised.has(definition.id))
    .map((definition) => ({
      ...definition,
      label:
        definition.id === "card"
          ? definition.label
          : advertised.get(definition.id)?.name || definition.label,
      detail: definition.id === "card" ? "Coinflow secure checkout" : null,
    }));

  if (crypto?.assets.some((asset) => asset.depositEnabled)) {
    methods.push({
      id: "crypto",
      kind: "crypto",
      label: "Crypto",
      detail: null,
      availability: "ready",
      currencies: [],
      unavailableReason: null,
    });
  }
  return methods;
}

function withdrawMethodViews(
  context: CashierWithdrawContext,
): CashierMethodView[] {
  return context.accounts.map((account) => ({
    id: account.token,
    kind: account.kind === "card" ? "card" : "bank",
    label: account.label,
    detail: account.detail,
    availability: "ready",
  }));
}

function unavailableSnapshot(message: string): CashierSnapshot {
  return {
    version: CASHIER_ADAPTER_VERSION,
    state: "unavailable",
    wallets: [],
    selectedWalletId: null,
    totalBalance: null,
    depositMethods: [],
    withdrawMethods: [],
    applePay: { state: "unavailable", message: null },
    message,
    withdraw: null,
    depositLimits: null,
  };
}

class CashierBackendAdapter implements CashierAdapter {
  readonly version = CASHIER_ADAPTER_VERSION;
  readonly applePay = buildApplePayAdapter();

  load = async (signal: AbortSignal): Promise<CashierSnapshot> => {
    const [limitsResult, withdrawResult, cryptoResult, googlePayResult] =
      await Promise.allSettled([
        apiGet<Record<string, unknown>>("/cashier/deposit-limits"),
        apiGet<Record<string, unknown>>(
          `/cashier/withdraw/methods?includeLimits=1&redirectUrl=${encodeURIComponent(withdrawReturnUrl())}`,
        ),
        apiGet<Record<string, unknown>>("/cashier/crypto/assets"),
        apiGet<Record<string, unknown>>("/cashier/google-pay/config"),
      ]);
    if (signal.aborted) throw new Error("aborted");

    // A capability/auth refusal on the core reads means the cashier is
    // closed for this session — surface the backend's reason, invent
    // nothing. (Crypto/Google Pay reads fail soft: the rail hides.)
    for (const result of [limitsResult, withdrawResult]) {
      if (
        result.status === "rejected" &&
        result.reason instanceof ApiError &&
        (result.reason.status === 401 || result.reason.status === 403)
      ) {
        return unavailableSnapshot(result.reason.message);
      }
    }
    if (
      limitsResult.status === "rejected" &&
      withdrawResult.status === "rejected"
    ) {
      throw limitsResult.reason;
    }

    const withdraw =
      withdrawResult.status === "fulfilled"
        ? normalizeWithdrawContext(withdrawResult.value)
        : normalizeWithdrawContext({});
    const depositLimits =
      limitsResult.status === "fulfilled"
        ? normalizeDepositLimits(limitsResult.value)
        : null;
    const cryptoAssets =
      cryptoResult.status === "fulfilled"
        ? normalizeCryptoAssets(cryptoResult.value)
        : [];
    const crypto = cryptoAssets.length ? { assets: cryptoAssets } : null;
    const googlePay =
      googlePayResult.status === "fulfilled"
        ? normalizeGooglePay(googlePayResult.value)
        : null;

    const applePay: CashierApplePayReadiness = applePaySupported()
      ? { state: "checking", message: null }
      : {
          state: "unsupported",
          message: "Apple Pay needs Safari on an Apple device.",
        };

    return {
      version: CASHIER_ADAPTER_VERSION,
      state: "ready",
      // Wallet display stays provider-derived through the fallback —
      // this backend holds no balance of its own.
      wallets: [],
      selectedWalletId: null,
      totalBalance: null,
      depositMethods: depositMethodViews(
        applePay,
        crypto,
        googlePay,
        depositLimits,
      ),
      withdrawMethods: withdrawMethodViews(withdraw),
      applePay,
      message: null,
      withdraw,
      depositLimits,
      crypto,
      googlePay,
    };
  };

  refresh = (signal: AbortSignal): Promise<CashierSnapshot> =>
    this.load(signal);

  beginDeposit = async (
    request: CashierActionRequest,
  ): Promise<CashierActionResult> => {
    const amountCents = centsFromAmount(request.amount);
    const data = await apiPost<{
      sessionId: string;
      status: string;
      redirectUrl: string;
    }>(
      "/cashier/deposit",
      { amountCents, currency: request.currency },
      undefined,
      { "X-Device-ID": deviceId() },
    );
    return {
      state: "started",
      message: null,
      redirectUrl: data.redirectUrl || null,
      sessionId: data.sessionId,
    };
  };

  beginDepositMethod = async (
    request: CashierActionRequest,
  ): Promise<CashierActionResult> => {
    const amountCents = centsFromAmount(request.amount);
    const data = await apiPost<{
      sessionId: string;
      status: string;
      redirectUrl: string;
    }>(
      "/cashier/deposit",
      {
        amountCents,
        currency: request.currency,
        methodId: request.methodId,
      },
      undefined,
      { "X-Device-ID": deviceId() },
    );
    return {
      state: "started",
      message: null,
      redirectUrl: data.redirectUrl || null,
      sessionId: data.sessionId,
    };
  };

  getDepositSession = async (
    sessionId: string,
  ): Promise<{ status: string }> => {
    const data = await apiGet<{ status: string }>(
      `/cashier/deposit/${sessionId}`,
    );
    return { status: data.status };
  };

  getWithdrawQuotes = async (
    amountCents: number,
    accountToken: string,
    speeds: readonly string[],
  ): Promise<readonly CashierQuote[]> => {
    const payload = await apiPost<Record<string, unknown>>(
      "/cashier/withdraw/quote",
      { amountCents, accountToken, speed: speeds[0] ?? "standard" },
    );
    if ("net_amount" in payload || "netAmount" in payload) {
      return quoteFromStubResponse(payload, amountCents);
    }
    return quotesFromCoinflowResponse(payload, amountCents, speeds);
  };

  beginWithdraw = async (
    request: CashierActionRequest,
  ): Promise<CashierActionResult> => {
    const amountCents = centsFromAmount(request.amount);
    const data = await apiPost<{
      sessionId: string;
      status: string;
      transactionId: string;
    }>("/cashier/withdraw", {
      amountCents,
      currency: request.currency,
      accountToken: request.methodId,
      speed: request.quote?.id ?? "standard",
    });
    return {
      state: data.status === "completed" ? "completed" : "pending",
      message:
        data.status === "pending_approval"
          ? "Withdrawal submitted — pending review."
          : null,
      sessionId: data.sessionId,
    };
  };

  createBankAuthSession = async (): Promise<string> => {
    const data = await apiPost<Record<string, unknown>>(
      "/cashier/withdraw/bank-auth-session",
      { redirectUrl: withdrawReturnUrl() },
    );
    const url = str(data.bank_auth_url) || str(data.bankAuthUrl);
    if (!url) throw new Error("No bank link session URL returned.");
    return url;
  };

  getDepositTotals = async (
    amountCents: number,
    method: "apple_pay" | "google_pay",
  ): Promise<{ subtotalCents: number; feeCents: number; totalCents: number }> =>
    apiPost<{ subtotalCents: number; feeCents: number; totalCents: number }>(
      "/cashier/apple-pay/totals",
      { amountCents, method },
    );

  googlePayCharge = async (
    amountCents: number,
    paymentData: Record<string, unknown>,
    idempotencyKey: string,
  ): Promise<CashierActionResult> => {
    // paymentData is single-use and short-lived: relay immediately, never
    // store or log it. A timeout retry reuses the SAME idempotency key.
    const data = await apiPost<{ status: string; sessionId?: string | null }>(
      "/cashier/google-pay/charge",
      {
        amountCents,
        paymentData,
        idempotencyKey,
        deviceId: deviceId(),
      },
    );
    return {
      state: data.status === "completed" ? "completed" : "pending",
      message: null,
      sessionId: data.sessionId ?? null,
    };
  };

  getCryptoDepositAddress = async (
    asset: string,
    network: string,
  ): Promise<CashierCryptoAddress> => {
    const data = await apiPost<Record<string, unknown>>(
      "/cashier/crypto/deposit-address",
      { asset, network },
    );
    return {
      address: str(data.address),
      memo: str(data.memo),
      network: str(data.network),
      asset: str(data.asset),
    };
  };

  cryptoWithdraw = async (request: {
    asset: string;
    network: string;
    amount: string;
    address: string;
    memo?: string;
    idempotencyKey?: string;
  }): Promise<CashierActionResult> => {
    const data = await apiPost<{
      transactionId: string;
      status: string;
      idempotencyKey: string;
    }>("/cashier/crypto/withdraw", {
      asset: request.asset,
      network: request.network,
      amount: request.amount,
      address: request.address,
      memo: request.memo ?? "",
      idempotencyKey: request.idempotencyKey ?? "",
    });
    return {
      state: data.status === "completed" ? "completed" : "pending",
      message:
        data.status === "pending_approval"
          ? "Withdrawal submitted — pending review."
          : null,
      sessionId: null,
    };
  };

  listTransactions = async (): Promise<readonly CashierTransactionView[]> => {
    const data = await apiGet<{
      transactions: {
        transactionId: string;
        kind: string;
        status: string;
        amountCents: number;
        currency: string;
        provider: string;
        paymentMethod: string | null;
        occurredAt: string;
      }[];
    }>("/cashier/transactions");
    return data.transactions.map((row) => ({
      transactionId: row.transactionId,
      kind: row.kind === "withdrawal" ? "withdrawal" : "deposit",
      status: row.status,
      amountCents: row.amountCents,
      currency: row.currency,
      provider: row.provider,
      paymentMethod: row.paymentMethod,
      occurredAt: row.occurredAt,
    }));
  };
}

export const cashierAdapter: CashierAdapter | null =
  new CashierBackendAdapter();
